import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Flower2, Gauge, Minus, Plus, Ruler, ShieldCheck, ShoppingBag, Sprout, Sun, Timer, Truck, Zap } from "lucide-react";
import api from "../lib/api";
import { useCart } from "../context/CartContext";
import ProductCard from "../components/ProductCard";
import { EASE, Reveal, SectionTag, money } from "../components/bits";
import SEO, { SITE_URL } from "../components/SEO";
import { PaymentBadges } from "../components/PaymentBadges";

const RATE = 0.32;
const monthlyCost = (w) => (w * 12 * 30) / 1000 * RATE;

const STAGE_ICONS = { Seedling: Sprout, Veg: Sun, Bloom: Flower2 };

export default function ProductPage() {
  const { slug } = useParams();
  const { addItem } = useCart();
  const initial = typeof window !== "undefined" ? window.__INITIAL_DATA__ : null;
  const hasInitial = initial?.product?.slug === slug;
  const [product, setProduct] = useState(() => (hasInitial ? initial.product : null));
  const [related, setRelated] = useState(() => (hasInitial ? initial.related || [] : []));
  const [vi, setVi] = useState(0);
  const [qty, setQty] = useState(1);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setProduct((p) => (p?.slug === slug ? p : null));
    setRelated((r) => (hasInitial ? r : []));
    setVi(0);
    setQty(1);
    setNotFound(false);
    api
      .get(`/products/${slug}`)
      .then((r) => setProduct(r.data))
      .catch(() => setNotFound(true));
    api.get("/products").then((r) => {
      const others = r.data.filter((p) => p.slug !== slug);
      setRelated(others.sort((a, b) => Number(b.featured) - Number(a.featured)).slice(0, 3));
    });
  }, [slug]);

  const variant = product?.variants[vi];
  const variantTags = (() => {
    if (!variant) return [];
    const parts = variant.name.split("·").slice(1).map((s) => s.trim()).filter(Boolean);
    if (parts.length) return parts;
    const n = variant.name;
    const tags = [];
    const w = n.match(/(\d+)\s*W/i);
    if (w) tags.push(`${w[1]}W`);
    if (/6500K/i.test(n)) tags.push("6500K Veg");
    if (/3500K/i.test(n)) tags.push("3500K Full Cycle");
    if (/660nm/i.test(n)) tags.push("+660nm Red");
    if (/U\.?V\.?/i.test(n)) tags.push("+UV");
    return tags;
  })();
  const hasEnergy = variant && variant.hps_equiv_watts > 0;
  const saveMo = hasEnergy ? monthlyCost(variant.hps_equiv_watts) - monthlyCost(variant.led_watts) : 0;
  const yieldBoost = hasEnergy && variant.yield_hps_g ? Math.round(((variant.yield_led_g - variant.yield_hps_g) / variant.yield_hps_g) * 100) : 0;

  if (notFound) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center" data-testid="product-not-found">
        <h1 className="font-display text-3xl font-bold text-[#F0FDF4]">That light has gone dark</h1>
        <p className="text-sm text-[#9FB8A8]">We couldn't find this product.</p>
        <Link to="/shop" className="rounded-full bg-[#39FF14] px-6 py-3 font-display text-sm font-bold text-[#050B07]">Back to the range</Link>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex min-h-screen items-center justify-center font-mono2 text-xs uppercase tracking-[0.3em] text-[#4ADE80]">
        Warming up the diodes…
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-5 pb-28 pt-24 sm:px-8" data-testid="product-page">
      <SEO
        title={`${product.name} — LED Grow Light | SuperTechLED`}
        description={`${product.name}: ${product.tagline} ${product.efficacy} efficacy, ${product.diodes}. Free AU express shipping and 2-year warranty. From ${money(Math.min(...product.variants.map((v) => v.price)))}.`}
        image={product.image}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: product.name,
          image: SITE_URL + product.image,
          description: product.description,
          brand: { "@type": "Brand", name: "SuperTechLED" },
          sku: product.sku,
          offers: product.variants.map((v) => ({
            "@type": "Offer",
            name: v.name,
            sku: v.sku,
            price: v.price,
            priceCurrency: "AUD",
            availability: v.stock === "sold_out" ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
          })),
        }}
      />
      <Link to="/shop" data-testid="product-back-link" className="inline-flex items-center gap-2 font-mono2 text-[11px] uppercase tracking-[0.25em] text-[#86EFAC] hover:text-[#39FF14]">
        <ArrowLeft className="h-3.5 w-3.5" /> The range
      </Link>

      <div className="mt-8 grid gap-12 lg:grid-cols-2">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl border border-[#39FF14]/20 bg-[#071009]">
            <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 100%, rgba(57,255,20,0.16), transparent 62%)" }} />
            {product.badge && (
              <span className="absolute left-5 top-5 z-10 rounded-full border border-[#39FF14]/30 bg-[#050B07]/85 px-3 py-1 font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#39FF14]">
                {product.badge}
              </span>
            )}
            <motion.img
              key={variant.sku}
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, ease: EASE }}
              src={variant.image || product.image}
              alt={`${product.name} — ${variant.name}`}
              data-testid="product-image"
              className="relative h-[380px] w-full object-contain p-10 sm:h-[460px]"
            />
            <AnimatePresence mode="wait">
              <motion.div
                key={variant.sku}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.35, ease: EASE }}
                className="absolute bottom-4 left-4 flex flex-wrap gap-2"
                data-testid="variant-tags"
              >
                {variant.stock === "sold_out" && (
                  <span className="rounded-full border border-red-400/40 bg-red-400/10 px-3 py-1 font-mono2 text-[10px] uppercase tracking-[0.18em] text-red-300 backdrop-blur-sm">
                    Sold out
                  </span>
                )}
                {variant.stock === "back_soon" && (
                  <span className="rounded-full border border-amber-400/40 bg-amber-400/10 px-3 py-1 font-mono2 text-[10px] uppercase tracking-[0.18em] text-amber-300 backdrop-blur-sm">
                    Back soon
                  </span>
                )}
                {variantTags.map((t) => (
                  <span key={t} className="rounded-full border border-[#39FF14]/35 bg-[#050B07]/85 px-3 py-1 font-mono2 text-[10px] uppercase tracking-[0.18em] text-[#39FF14] backdrop-blur-sm">
                    {t}
                  </span>
                ))}
              </motion.div>
            </AnimatePresence>
          </div>
          <div className="mt-5 grid grid-cols-3 gap-3">
            {[
              { icon: Truck, label: "Free AU express" },
              { icon: ShieldCheck, label: "2-year warranty" },
              { icon: Timer, label: "30-day returns" },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2 rounded-xl border border-[#39FF14]/12 bg-[#0A150F] px-3 py-2.5">
                <Icon className="h-4 w-4 shrink-0 text-[#39FF14]" />
                <span className="text-[11px] text-[#C9DCD0]">{label}</span>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-[#4ADE80]">{product.series}</div>
          <h1 className="font-display mt-2 text-3xl font-black tracking-tight text-[#F0FDF4] sm:text-4xl lg:text-5xl" data-testid="product-name">
            {product.name}
          </h1>
          <p className="mt-3 text-base leading-relaxed text-[#9FB8A8]">{product.description}</p>

          <div className="mt-7">
            <div className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#4ADE80]">Choose your size</div>
            <div className="mt-3 flex flex-wrap gap-2">
              {product.variants.map((v, i) => (
                <button
                  key={v.sku}
                  data-testid={`variant-${v.sku}`}
                  onClick={() => setVi(i)}
                  className={`rounded-full px-5 py-2.5 font-mono2 text-[11px] tracking-wider transition-all ${
                    vi === i
                      ? "bg-[#39FF14] font-bold text-[#050B07] shadow-[0_0_24px_rgba(57,255,20,0.35)]"
                      : `border border-[#39FF14]/25 text-[#86EFAC] hover:border-[#39FF14]/60 ${v.stock === "sold_out" ? "opacity-50" : ""}`
                  }`}
                >
                  {v.name}
                  {v.stock === "sold_out" && <span className="ml-1.5 text-red-400">· sold out</span>}
                  {v.stock === "back_soon" && <span className={`ml-1.5 ${vi === i ? "text-[#050B07]/70" : "text-amber-300"}`}>· back soon</span>}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-5">
            <div className="font-display text-4xl font-black text-[#F0FDF4]" data-testid="product-price">
              {money(variant.price)}
            </div>
            <div className="flex items-center gap-3 rounded-full border border-[#39FF14]/25 px-3 py-2">
              <button data-testid="qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="text-[#86EFAC] hover:text-[#39FF14]">
                <Minus className="h-4 w-4" />
              </button>
              <span className="w-6 text-center font-mono2 text-sm" data-testid="qty-value">{qty}</span>
              <button data-testid="qty-plus" onClick={() => setQty((q) => Math.min(20, q + 1))} className="text-[#86EFAC] hover:text-[#39FF14]">
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>

          <button
            data-testid={`add-to-cart-${product.slug}`}
            onClick={() => addItem(product, variant, qty)}
            disabled={variant.stock === "sold_out"}
            className={`group mt-6 flex w-full items-center justify-center gap-2 rounded-full py-4 font-display text-sm font-bold transition-shadow sm:w-auto sm:px-10 ${
              variant.stock === "sold_out"
                ? "cursor-not-allowed bg-red-400/15 text-red-300/70"
                : "bg-[#39FF14] text-[#050B07] hover:shadow-[0_0_40px_rgba(57,255,20,0.45)]"
            }`}
          >
            <ShoppingBag className="h-4 w-4" />
            {variant.stock === "sold_out" ? "Sold out" : `Add to cart — ${money(variant.price * qty)}`}
          </button>
          <div className="mt-4 flex items-center gap-3">
            <PaymentBadges />
            <span className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-[#4ADE80]/60">Secure checkout</span>
          </div>
          {variant.stock === "back_soon" && (
            <p className="mt-3 text-xs text-amber-300" data-testid="back-soon-note">
              Back soon — order now and we'll ship the moment it lands.
            </p>
          )}

          <div className="mt-8 flex flex-wrap gap-2">
            {product.stages.map((s) => {
              const Icon = STAGE_ICONS[s] || Sun;
              return (
                <span key={s} className="flex items-center gap-1.5 rounded-full border border-[#39FF14]/20 bg-[#0A150F] px-3 py-1.5 text-[11px] text-[#C9DCD0]">
                  <Icon className="h-3.5 w-3.5 text-[#39FF14]" />{` ${s}`}
                </span>
              );
            })}
          </div>
        </Reveal>
      </div>

      <Reveal className="mt-20">
        <SectionTag>Tech sheet</SectionTag>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="spec-grid">
          {[
            { icon: Zap, label: "Power draw", value: `${variant.led_watts}W` },
            { icon: Gauge, label: "Efficacy", value: product.efficacy },
            { icon: Ruler, label: "Veg coverage", value: variant.coverage_veg },
            { icon: Ruler, label: "Bloom coverage", value: variant.coverage_bloom },
            { icon: Sun, label: "Diodes", value: product.diodes },
            { icon: Zap, label: "Driver", value: product.driver },
            { icon: Timer, label: "Dimming", value: product.dimmable ? "Yes · dimmable" : "Fixed output" },
            { icon: ShieldCheck, label: "Warranty", value: "2 years · AU" },
          ].map(({ icon: Icon, label, value }) => (
            <div key={label} className="rounded-2xl border border-[#39FF14]/12 bg-[#0A150F] p-5">
              <Icon className="h-4 w-4 text-[#39FF14]" />
              <div className="mt-3 font-mono2 text-[9px] uppercase tracking-[0.25em] text-[#4ADE80]/80">{label}</div>
              <div className="mt-1 text-sm font-semibold text-[#F0FDF4]">{value}</div>
            </div>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {product.spectrum.map((s) => (
            <span key={s} className="rounded-full bg-[#0E1C13] px-3 py-1.5 font-mono2 text-[10px] uppercase tracking-[0.18em] text-[#86EFAC]">
              {s}
            </span>
          ))}
        </div>
      </Reveal>

      {hasEnergy && (
        <Reveal className="mt-16">
          <div className="glass rounded-3xl p-8 sm:p-10" data-testid="energy-panel">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <SectionTag>{`Vs the old ${variant.hps_equiv_watts}W HPS`}</SectionTag>
                <h2 className="font-display mt-3 text-2xl font-bold text-[#F0FDF4] sm:text-3xl">
                  Same harvest. <span className="text-[#39FF14]">Smaller bill.</span>
                </h2>
              </div>
              <div className="text-right">
                <div className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#4ADE80]">You save</div>
                <div className="font-display text-3xl font-black text-[#39FF14]" data-testid="energy-savings">
                  {`${money(Math.round(saveMo))}/mo · ${money(Math.round(saveMo * 12))}/yr`}
                </div>
                <div className="text-xs text-[#9FB8A8]">at $0.32/kWh · 12h flower cycle, before cooling savings</div>
              </div>
            </div>
            <div className="mt-8 grid gap-8 sm:grid-cols-2">
              <div>
                <div className="mb-3 flex items-center justify-between">
                  <span className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#4ADE80]">Yield per harvest</span>
                  <span className="font-mono2 text-[10px] text-[#39FF14]" data-testid="yield-boost">{`+${yieldBoost}%`}</span>
                </div>
                <div className="space-y-3">
                  <div>
                    <div className="mb-1 flex justify-between text-xs text-[#9FB8A8]">
                      <span>{`SuperTechLED ${variant.led_watts}W`}</span><span>{`~${variant.yield_led_g}g`}</span>
                    </div>
                    <div className="h-3 rounded-full bg-[#050B07]">
                      <motion.div
                        key={`y-${variant.sku}`}
                        initial={{ width: 0 }}
                        whileInView={{ width: "100%" }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, ease: EASE }}
                        className="h-full rounded-full bg-[#39FF14]"
                      />
                    </div>
                  </div>
                  <div>
                    <div className="mb-1 flex justify-between text-xs text-[#9FB8A8]">
                      <span>{`${variant.hps_equiv_watts}W HPS`}</span><span>{`~${variant.yield_hps_g}g`}</span>
                    </div>
                    <div className="h-3 rounded-full bg-[#050B07]">
                      <motion.div
                        key={`h-${variant.sku}`}
                        initial={{ width: 0 }}
                        whileInView={{ width: `${(variant.yield_hps_g / variant.yield_led_g) * 100}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, ease: EASE }}
                        className="h-full rounded-full bg-red-400/60"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <div className="mb-3 font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#4ADE80]">Monthly running cost</div>
                <div className="space-y-3">
                  <div>
                    <div className="mb-1 flex justify-between text-xs text-[#9FB8A8]">
                      <span>SuperTechLED</span><span>{money(Math.round(monthlyCost(variant.led_watts)))}</span>
                    </div>
                    <div className="h-3 rounded-full bg-[#050B07]">
                      <motion.div
                        key={`c-${variant.sku}`}
                        initial={{ width: 0 }}
                        whileInView={{ width: `${(variant.led_watts / variant.hps_equiv_watts) * 100}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, ease: EASE }}
                        className="h-full rounded-full bg-[#39FF14]"
                      />
                    </div>
                  </div>
                  <div>
                    <div className="mb-1 flex justify-between text-xs text-[#9FB8A8]">
                      <span>HPS</span><span>{money(Math.round(monthlyCost(variant.hps_equiv_watts)))}</span>
                    </div>
                    <div className="h-3 rounded-full bg-[#050B07]">
                      <motion.div
                        key={`ch-${variant.sku}`}
                        initial={{ width: 0 }}
                        whileInView={{ width: "100%" }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, ease: EASE }}
                        className="h-full rounded-full bg-red-400/60"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      )}

      <Reveal className="mt-20">
        <div className="flex items-end justify-between">
          <div>
            <SectionTag>Keep browsing</SectionTag>
            <h2 className="font-display mt-3 text-2xl font-bold text-[#F0FDF4] sm:text-3xl">Growers also run</h2>
          </div>
          <Link to="/shop" className="group hidden items-center gap-2 font-mono2 text-[11px] uppercase tracking-[0.25em] text-[#39FF14] sm:flex">
            All lights <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {related.map((p, i) => (
            <ProductCard key={p.slug} p={p} i={i} />
          ))}
        </div>
      </Reveal>
    </div>
  );
}
